// `buildUrl` — turn a route entry + args into a concrete URL string.
//
// Subsumes the hand-rolled `encodeURIComponent` + `URLSearchParams` scattered
// across the apps' inline fetch call sites and the legacy `lib/api-client/*`.

import type { QueryShape, RouteEntry } from './types';

const PARAM_RE = /:([A-Za-z0-9_]+)\*?/g; // `:id` or catch-all `:all*`

export interface BuildUrlArgs {
  /** Path-param values. Keys must cover every `:name` in the template. */
  params?: Record<string, string | string[]>;
  /** Optional query params. `null`/`undefined` values are dropped. */
  query?: QueryShape;
}

/**
 * Build a relative URL from a registry entry's path template and the supplied
 * params/query. Path params are `encodeURIComponent`-escaped; a catch-all
 * (`:name*`) value may be an array, joined by `/` with each segment escaped.
 *
 * @throws if a `:param` in the template has no value in `args.params`.
 */
export function buildUrl(entry: Pick<RouteEntry, 'path'>, args: BuildUrlArgs = {}): string {
  const params = args.params ?? {};

  const path = entry.path.replace(PARAM_RE, (_match, name: string) => {
    const value = params[name];
    if (value == null) {
      throw new Error(`[api-registry] missing path param '${name}' for '${entry.path}'`);
    }
    if (Array.isArray(value)) {
      return value.map((seg) => encodeURIComponent(seg)).join('/');
    }
    return encodeURIComponent(value);
  });

  const qs = buildQueryString(args.query);
  return qs ? `${path}?${qs}` : path;
}

/** Serialize a query object, dropping `null`/`undefined`, coercing to strings. */
export function buildQueryString(query?: QueryShape): string {
  if (!query) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value == null) continue;
    sp.append(key, String(value));
  }
  return sp.toString();
}
